import './ClassList.less';

import { DeleteFilled, DragOutlined } from '@ant-design/icons';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useDefaultClassCreate,
  useDefaultClassRemove,
  useDefaultClassUpdate,
  useVehicleDefaultClassUpdateByCarrier,
  useVehicleDefaultClassUpdateGlobal,
} from '../../hooks/apiHooks';
import Container from '../common/Container';
import EditButton from '../common/EditButton';
import ListHeader from '../common/ListHeader';
import VehicleClassModal from '../modals/VehicleClassModal';
import DraggableVehicleChip from './DraggableVehicleChip';
import DroppableRateClass from './DroppableRateClass';
import DroppableUnassignedContainer from './DroppableUnassignedContainer';

/*
interface IProps {
  id: string;
  carrier?: Carrier;
  vehicles: Vehicle[];
  defaultClasses: DefaultClass[];
}
*/

const RateClassList = ({ carrier, defaultClasses, id, vehicleDefaultClasses, vehicles, }) => {
  const updateMutationByCarrier = useVehicleDefaultClassUpdateByCarrier(carrier?.id || 0);
  const updateMutationGlobal = useVehicleDefaultClassUpdateGlobal();

  const createClassMutation = useDefaultClassCreate();
  const updateClassMutation = useDefaultClassUpdate();
  const removeClassMutation = useDefaultClassRemove();

  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);

  const [deletableClass, setDeletableClass] = useState(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [classNames, setClassNames] = useState({});
  const [formValues, setFormValues] = useState({});
  const [activeId, setActiveId] = useState(null);
  const originalFormData = useRef({});
  const originalClassNames = useRef({});

  const initializeClassNames = useCallback(() => {
    const names = {};
    defaultClasses.forEach((dc) => {
      names[dc.id] = dc.name;
    });
    return names;
  }, [defaultClasses]);

  // Convert vehicleDefaultClasses to form data format
  const convertToFormData = useCallback(() => {
    // vehicleDefaultClasses is now already in the correct format: { defaultClassId: [vehicleIds] }
    // We just need to ensure the keys are properly formatted and return a copy
    const formData = {};
    if (vehicleDefaultClasses && typeof vehicleDefaultClasses === 'object') {
      Object.entries(vehicleDefaultClasses).forEach(([defaultClassId, vehicleIds]) => {
        if (Array.isArray(vehicleIds)) {
          formData[defaultClassId] = [...vehicleIds]; // Create a copy of the array
        }
      });
    }
    return formData;
  }, [vehicleDefaultClasses]);

  // Convert form data back to vehicleDefaultClasses format
  const convertFromFormData = useCallback((formData) => {
    const result = [];
    Object.entries(formData).forEach(([defaultClassId, vehicleIds]) => {
      if (Array.isArray(vehicleIds)) {
        vehicleIds.forEach((vehicleId) => {
          result.push({
            carrierId: carrier?.id,
            defaultClassId: parseInt(defaultClassId),
            vehicleId: vehicleId,
          });
        });
      }
    });
    return result;
  }, [carrier?.id]);

  // find classes with changed names
  const changedClassNames = useMemo(() => {
    const changes = [];
    defaultClasses.forEach((dc) => {
      if (classNames[dc.id] && classNames[dc.id] !== dc.name) {
        changes.push({ id: dc.id, name: classNames[dc.id] });
      }
    });
    return changes;
  }, [classNames, defaultClasses]);

  // checking if any class name is empty
  const hasEmptyClassName = useMemo(() => {
    return defaultClasses.some((dc) => !classNames[dc.id]?.trim());
  }, [classNames, defaultClasses]);

  const onSubmit = useCallback(
    async (formData) => {
      // Prevent save if any class name is empty
      if (hasEmptyClassName) {
        return;
      }

      // First, update any changed class names
      for (const changedClass of changedClassNames) {
        await updateClassMutation.mutateAsync(changedClass);
      }

      // then update vehicle default classes
      const vehicleDefaultClassData = convertFromFormData(formData);
      if (carrier) {
        updateMutationByCarrier.mutate(vehicleDefaultClassData);
      } else {
        updateMutationGlobal.mutate(vehicleDefaultClassData);
      }
    },
    [convertFromFormData, updateMutationByCarrier, updateMutationGlobal, carrier, changedClassNames, updateClassMutation, hasEmptyClassName]
  );

  const handleSave = useCallback(() => {
    if (hasEmptyClassName) {
      return;
    }
    form.submit();
  }, [form, hasEmptyClassName]);

  const handleEdit = useCallback(() => {
    // store orig values before editing
    originalFormData.current = form.getFieldsValue();
    originalClassNames.current = { ...classNames };
    setFormValues(originalFormData.current);
    setIsEditing(true);
  }, [form, classNames]);

  const handleCancel = useCallback(() => {
    // revert form values
    form.setFieldsValue(originalFormData.current);
    setFormValues(originalFormData.current);
    // revert class names
    setClassNames(originalClassNames.current);
    setDeletableClass(undefined);
    setIsEditing(false);
  }, [form]);

  const onAddClass = useCallback(
    (classification) => {
      createClassMutation.mutate(classification);
    },
    [createClassMutation]
  );

  const handleClassNameChange = useCallback((classId, newName) => {
    setClassNames((prev) => ({
      ...prev,
      [classId]: newName,
    }));
  }, []);

  const onDeleteClass = useCallback(() => {
    if (deletableClass) {
      removeClassMutation.mutate(deletableClass);
    }
  }, [deletableClass, removeClassMutation]);

  // Set form values when data loads
  useEffect(() => {
    const formData = convertToFormData();
    form.setFieldsValue(formData);
    setFormValues(formData);
  }, [convertToFormData, form, vehicleDefaultClasses]);

  // Initialize class names when defaultClasses change
  useEffect(() => {
    setClassNames(initializeClassNames());
  }, [initializeClassNames]);

  // Reset deletable class when editing mode is off
  useEffect(() => {
    if (!isEditing) {
      setDeletableClass(undefined);
    }
  }, [isEditing]);

  // Close add modal on success
  useEffect(() => {
    if (createClassMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createClassMutation.isSuccess]);

  // Turn off editing mode when classes are successfully saved
  useEffect(() => {
    if (updateMutationByCarrier.isSuccess || updateMutationGlobal.isSuccess) {
      setIsEditing(false);
    }
  }, [updateMutationByCarrier.isSuccess, updateMutationGlobal.isSuccess]);

  const isSaving = updateMutationByCarrier.isLoading || updateMutationGlobal.isLoading || updateClassMutation.isLoading;

  const leftColumnClasses = useMemo(() => {
    return defaultClasses.filter((_, index) => index % 2 === 0);
  }, [defaultClasses]);

  const rightColumnClasses = useMemo(() => {
    return defaultClasses.filter((_, index) => index % 2 === 1);
  }, [defaultClasses]);

  const currentVehicleMap = useMemo(() => {
    if (isEditing) {
      return formValues;
    }

    return convertToFormData();
  }, [convertToFormData, formValues, isEditing]);

  const assignedVehicleIds = useMemo(() => {
    const allAssigned = Object.values(currentVehicleMap || {}).flat();
    return new Set(allAssigned);
  }, [currentVehicleMap]);

  const unassignedVehicles = useMemo(() => {
    return vehicles.filter((v) => !assignedVehicleIds.has(v.id));
  }, [vehicles, assignedVehicleIds]);

  const getAssignedVehiclesForClass = useCallback(
    (defaultClassId) => {
      const vehicleIds = formValues[defaultClassId] || [];
      return vehicles.filter((v) => vehicleIds.includes(v.id));
    },
    [vehicles, formValues],
  );

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || !isEditing) {
        return;
      }

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!activeData || !overData) {
        return;
      }

      // dragging vehicle type from unassigned to a rate class
      if (activeData.type === 'vehicle' && overData.type === 'rate-class') {
        const vehicleId = activeData.vehicle.id;
        const targetClassId = overData.classId;

        // get current assignments
        const currentValues = form.getFieldsValue();
        const currentClassVehicles = currentValues[targetClassId] || [];

        // Don't add if already assigned
        if (currentClassVehicles.includes(vehicleId)) {
          return;
        }

        // Remove from previous class if assigned to another class
        const updatedValues = { ...currentValues };
        Object.keys(updatedValues).forEach((classId) => {
          if (classId !== targetClassId && Array.isArray(updatedValues[classId])) {
            updatedValues[classId] = updatedValues[classId].filter((id) => id !== vehicleId);
          }
        });

        // add to target class
        updatedValues[targetClassId] = [...currentClassVehicles, vehicleId];

        form.setFieldsValue(updatedValues);
        setFormValues(updatedValues);
      }

      // Moving a vehicle type back to unassigned
      if (activeData.type === 'vehicle' && overData.type === 'unassigned') {
        const vehicleId = activeData.vehicle.id;

        const currentValues = form.getFieldsValue();
        const updatedValues = { ...currentValues };

        Object.keys(updatedValues).forEach((classId) => {
          if (Array.isArray(updatedValues[classId])) {
            updatedValues[classId] = updatedValues[classId].filter((id) => id !== vehicleId);
          }
        });

        form.setFieldsValue(updatedValues);
        setFormValues(updatedValues);
      }
    },
    [form, isEditing],
  );

  const handleRemoveVehicle = useCallback(
    (defaultClassId, vehicleId) => {
      const currentValues = form.getFieldsValue();
      const currentClassVehicles = (currentValues[defaultClassId] || []).filter((id) => id !== vehicleId);
      const updatedValues = {
        ...currentValues,
        [defaultClassId]: currentClassVehicles,
      };
      form.setFieldsValue(updatedValues);
      setFormValues(updatedValues);
    },
    [form],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  return (
    <Container id={id}>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <ListHeader>

          <Space style={{ marginLeft: 'auto' }}>
            {isEditing ? (
              <>
                <EditButton
                  disabled={hasEmptyClassName}
                  loading={isSaving}
                  onClick={handleSave}
                >
                  Save
                </EditButton>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
              </>
            ) : (
              <EditButton onClick={handleEdit}>
                Edit
              </EditButton>
            )}
            <Button
              ghost
              style={{ width: 120 }}
              type="primary"
              onClick={() => setShowAddModal(true)}
            >
              Add Class
            </Button>
          </Space>
        </ListHeader>

        <DndContext sensors={sensors} onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <div className="class-list__grid">
            {/* unassigned vehicle types */}
            <div className="class-list__unassigned-column">
              <div className="class-list__unassigned-header">
                <Typography.Text strong>Unassigned Vehicle Types</Typography.Text>
              </div>
              <DroppableUnassignedContainer isEditing={isEditing}>
                {unassignedVehicles.length === 0 ? (
                  <Typography.Text
                    style={{ display: 'block', padding: '20px', textAlign: 'center' }}
                    type="secondary"
                  >
                    No unassigned vehicles
                  </Typography.Text>
                ) : (
                  <Space wrap size={[8, 8]}>
                    {unassignedVehicles.map((vehicle) => (
                      <DraggableVehicleChip key={vehicle.id} isEditing={isEditing} vehicle={vehicle} />
                    ))}
                  </Space>
                )}
              </DroppableUnassignedContainer>
            </div>

            {/* left column rate classes */}
            <div className="class-list__rate-classes-column">
              {leftColumnClasses.map((defaultClass) => (
                <div key={defaultClass.id} className="class-list__rate-class-container">
                  <div className="class-list__rate-class-header">
                    <Space>
                      {isEditing ? (
                        <Input
                          placeholder="Enter class name"
                          size="middle"
                          status={!classNames[defaultClass.id]?.trim() ? 'error' : undefined}
                          style={{ width: 155 }}
                          value={classNames[defaultClass.id] || ''}
                          onChange={(e) => handleClassNameChange(defaultClass.id, e.target.value)}
                        />
                      ) : (
                        <span>{defaultClass.name}</span>
                      )}
                      {isEditing && (
                        <Popconfirm
                          okButtonProps={{ loading: removeClassMutation.isLoading }}
                          open={deletableClass?.id === defaultClass.id}
                          title="Are you sure to delete this Rate Class?"
                          onCancel={() => setDeletableClass(undefined)}
                          onConfirm={onDeleteClass}
                        >
                          <Tooltip title="Delete Rate Class">
                            <DeleteFilled
                              style={{ color: '#ff4d4f', cursor: 'pointer' }}
                              onClick={() => setDeletableClass(defaultClass)}
                            />
                          </Tooltip>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                  <Form.Item
                    key={defaultClass.id}
                    noStyle
                    name={defaultClass.id}
                  >
                    <DroppableRateClass
                      assignedVehicles={getAssignedVehiclesForClass(defaultClass.id)}
                      classification={defaultClass}
                      isEditing={isEditing}
                      vehicles={vehicles}
                      onRemoveVehicle={handleRemoveVehicle}
                    />
                    {/* hidden Select to bind form data */}
                    <Select
                      className="class-list__vehicle-select"
                      disabled={!isEditing}
                      mode="multiple"
                      placeholder="Vehicle Types"
                      style={{ display: 'none' }}
                    >
                      {vehicles.map((vehicle) => (
                        <Select.Option key={vehicle.id} value={vehicle.id}>
                          {vehicle.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              ))}
            </div>

            {/* right column rate classes */}
            <div className="class-list__rate-classes-column">
              {rightColumnClasses.map((defaultClass) => (
                <div key={defaultClass.id} className="class-list__rate-class-container">
                  <div className="class-list__rate-class-header">
                    <Space>
                      {isEditing ? (
                        <Input
                          placeholder="Enter class name"
                          size="middle"
                          status={!classNames[defaultClass.id]?.trim() ? 'error' : undefined}
                          style={{ width: 155 }}
                          value={classNames[defaultClass.id] || ''}
                          onChange={(e) => handleClassNameChange(defaultClass.id, e.target.value)}
                        />
                      ) : (
                        <span>{defaultClass.name}</span>
                      )}
                      {isEditing && (
                        <Popconfirm
                          okButtonProps={{ loading: removeClassMutation.isLoading }}
                          open={deletableClass?.id === defaultClass.id}
                          title="Are you sure to delete this Rate Class?"
                          onCancel={() => setDeletableClass(undefined)}
                          onConfirm={onDeleteClass}
                        >
                          <Tooltip title="Delete Rate Class">
                            <DeleteFilled
                              style={{ color: '#ff4d4f', cursor: 'pointer' }}
                              onClick={() => setDeletableClass(defaultClass)}
                            />
                          </Tooltip>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                  <Form.Item
                    key={defaultClass.id}
                    noStyle
                    name={defaultClass.id}
                  >
                    <DroppableRateClass
                      assignedVehicles={getAssignedVehiclesForClass(defaultClass.id)}
                      classification={defaultClass}
                      isEditing={isEditing}
                      vehicles={vehicles}
                      onRemoveVehicle={handleRemoveVehicle}
                    />
                    {/* hidden Select to bind form data */}
                    <Select
                      className="class-list__vehicle-select"
                      disabled={!isEditing}
                      mode="multiple"
                      placeholder="Vehicle Types"
                      style={{ display: 'none' }}
                    >
                      {vehicles.map((vehicle) => (
                        <Select.Option key={vehicle.id} value={vehicle.id}>
                          {vehicle.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              ))}
            </div>
          </div>
          <DragOverlay>
            {activeId ? (() => {
              const vehicleId = activeId.toString().replace('vehicle-', '');
              const draggedVehicle = vehicles.find((v) => String(v.id) === vehicleId);
              return draggedVehicle ? (
                <Tag className="class-list__vehicle-chip">
                  <Space size={4}>
                    {isEditing && <DragOutlined />}
                    {draggedVehicle.name}
                  </Space>
                </Tag>
              ) : null;
            })() : null}
          </DragOverlay>
        </DndContext>
      </Form>

      {showAddModal && (
        <VehicleClassModal
          error={createClassMutation.error}
          submitting={createClassMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAddClass}
        />
      )}
    </Container>
  );
};

export default RateClassList;
